// Catalog.ID Client — Internal API for cross-module Registry signing
// ============================================================================
//
// Wraps `POST /api/internal/sign-registry-canonical`. The endpoint is the
// authoritative source of canonical-bytes layout for outbound `registry-request`
// envelopes — it builds them via the server-side `CrossModuleCanonical` and
// signs with the catalog.id `ModuleIdentity` Ed25519 key (which lives only in
// the cld-id helper-service process, never in the browser).
//
// The browser supplies the per-mode field values; the server emits the
// signature + the ModuleIdentity pubkey. The browser then attaches both to
// the wire envelope before shipping it via the dashboard hub.
//
// IMPORTANT: this endpoint lives ONLY on the dashboard-mode helper service
// (e.g. localhost:61534), NOT on the real catalog.id server (localhost:5100
// in dev / catalog.id in prod). It must therefore be called as a same-origin
// relative URL — going through the shared `api` client would route it via
// `getApiBase()` to the real server, which 404s. That 404 surfaces as
// `sign-challenge-failed:Not Found` in registry's publish-key flow because the
// step 5 sign-challenge round-trip needs THIS endpoint to build the canonical
// payload before sending it through the dashboard hub.
//
// Loopback-only — the helper service gates this endpoint via
// LoopbackOriginFilter. No session token is enforced in v1, matching the rest
// of the catalog.id internal surface.
// ============================================================================

import { ApiError } from './client';

export type SignRegistryMode = 'sign-challenge';

export interface SignRegistryRequest {
    mode: SignRegistryMode;
    /** Receiver's module id (e.g. "registry"). */
    to: string;
    requestId: string;
    /** Sender's module id (always "cid" in v1). */
    requesterId: string;
    /** RFC 3339 UTC timestamp the sender stamps on the request. */
    requestedAt: string;
    // sign-challenge fields:
    claimUuid?: string;
    fingerprint?: string;
    challengeBase64?: string;
}

export interface SignRegistryResponse {
    /** Base64 Ed25519 signature over the server-built canonical bytes. */
    signature: string;
    /** Base64 Ed25519 pubkey of the catalog.id ModuleIdentity. */
    signerPubkey: string;
}

export async function signRegistryCanonical(req: SignRegistryRequest): Promise<SignRegistryResponse> {
    const resp = await fetch('/api/internal/sign-registry-canonical', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
    });
    if (!resp.ok) {
        let body: unknown;
        try { body = await resp.json(); } catch { body = { error: resp.statusText }; }
        throw new ApiError(resp.status, body);
    }
    return await resp.json() as SignRegistryResponse;
}
